import React, { useEffect, useMemo, useState } from "react";

import Tree from "react-d3-tree";

import axios from "axios";

import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";

import {
  BiSolidSun,
  BiSolidMoon,
} from "react-icons/bi";

import {
  FaArrowLeft,
  FaSearch,
  FaRedo,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
  FaUsers,
  FaTimes,
  FaCopy,
  FaInfoCircle,
  FaUser,
} from "react-icons/fa";

import UserDetails from "./UserDetails";

const Cedvel = () => {
  const navigate = useNavigate();

  const { userInfo } = useSelector(
    (state) => state.auth
  );

  const [treeData, setTreeData] = useState(null);

  const [translate, setTranslate] = useState({
    x: window.innerWidth / 2,
    y: 110,
  });

  const [zoom, setZoom] = useState(0.7);

  const [allUsers, setAllUsers] = useState([]);

  const [searchInput, setSearchInput] = useState("");

  const [suggestions, setSuggestions] = useState([]);

  const [selectedUser, setSelectedUser] = useState(null);

  const [loading, setLoading] = useState(true);

  const [searchLoading, setSearchLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const [isDarkMode, setIsDarkMode] = useState(false);

  const [darkMode, setDarkMode] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  const [copiedReferralCode, setCopiedReferralCode] = useState(null);

  const [detailsUserId, setDetailsUserId] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | BODY SCROLL
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | DARK MODE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const html = document.documentElement;

    const checkTheme = () => {
      const isDark = html.classList.contains("dark");

      setDarkMode(isDark);
      setIsDarkMode(isDark);
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);

    observer.observe(html, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");

      setDarkMode(true);
      setIsDarkMode(true);
    }

    if (savedTheme === "light") {
      document.documentElement.classList.remove("dark");

      setDarkMode(false);
      setIsDarkMode(false);
    }
  }, []);

  const toggleDarkMode = () => {
    const html = document.documentElement;

    if (html.classList.contains("dark")) {
      html.classList.remove("dark");

      localStorage.setItem("theme", "light");

      setDarkMode(false);
      setIsDarkMode(false);
    } else {
      html.classList.add("dark");

      localStorage.setItem("theme", "dark");

      setDarkMode(true);
      setIsDarkMode(true);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | API BASE
  |--------------------------------------------------------------------------
  */

  const apiBaseUrl = useMemo(() => {
    return (
      process.env.REACT_APP_API_BASE_URL ||
      "https://artim.shop/backendtest_9"
    ).replace(/\/+$/, "");
  }, []);

  /*
  |--------------------------------------------------------------------------
  | TREE BUILDER
  |--------------------------------------------------------------------------
  */

  const buildTree = (rootUser, users) => {
    if (!rootUser) {
      return null;
    }

    const normalize = (user) => {
      if (!user) {
        return null;
      }

      return {
        ...user,
        _id: user._id || user.id,
      };
    };

    const normalizedRoot = normalize(rootUser);

    const findChildren = (parent) => {
      if (!parent || !parent.referralCode) {
        return {
          left: null,
          right: null,
        };
      }

      const children = users.filter(
        (user) =>
          user &&
          user.referredBy === parent.referralCode &&
          !user.isDeleted
      );

      let left = null;
      let right = null;

      children.forEach((child) => {
        if (child.side === "left" && !left) {
          left = child;
        }

        if (child.side === "right" && !right) {
          right = child;
        }
      });

      return {
        left,
        right,
      };
    };

    const createNode = (user, isRoot = false) => {
      if (!user) {
        return null;
      }

      const normalizedUser = normalize(user);

      const children = findChildren(normalizedUser);

      const node = {
        name: normalizedUser.name || "Adsız istifadəçi",

        attributes: {
          UserId: normalizedUser._id || "",
          Kod: normalizedUser.referralCode || "-",

          Ödəniş: normalizedUser.payment
            ? "Ödəniş edib"
            : "Ödəniş etməyib",

          Qol:
            normalizedUser.side === "left"
              ? "Sol"
              : normalizedUser.side === "right"
              ? "Sağ"
              : isRoot
              ? "Ana qol"
              : "-",

          Photo: normalizedUser.photo || "",
          Email: normalizedUser.email || "",
          Phone: normalizedUser.phone || "",
          Earnings: normalizedUser.earnings ?? 0,
          ReferralCount: normalizedUser.referralCount ?? 0,
        },

        children: [],
      };

      if (children.left) {
        const leftNode = createNode(children.left);

        if (leftNode) {
          node.children.push(leftNode);
        }
      }

      if (children.right) {
        const rightNode = createNode(children.right);

        if (rightNode) {
          node.children.push(rightNode);
        }
      }

      return node;
    };

    return [createNode(normalizedRoot, true)];
  };

  /*
  |--------------------------------------------------------------------------
  | FETCH TREE DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let cancelled = false;

    const loadTree = async () => {
      if (!userInfo?.referralCode) {
        setTreeData(null);
        setAllUsers([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        const requests = await Promise.allSettled([
          axios.get(
            `${apiBaseUrl}/api/users/admin/${encodeURIComponent(
              userInfo.referralCode
            )}`,
            {
              withCredentials: true,
            }
          ),

          axios.get(
            `${apiBaseUrl}/api/users/user/${encodeURIComponent(
              userInfo.referralCode
            )}`,
            {
              withCredentials: true,
            }
          ),
        ]);

        if (cancelled) {
          return;
        }

        const usersMap = new Map();

        requests.forEach((result) => {
          if (result.status !== "fulfilled") {
            return;
          }

          const data = result.value?.data;

          const users = Array.isArray(data?.users)
            ? data.users
            : Array.isArray(data)
            ? data
            : data?.user
            ? [data.user]
            : [];

          users.forEach((user) => {
            if (!user) {
              return;
            }

            const id = user._id || user.id;

            if (id) {
              usersMap.set(String(id), user);
            }
          });
        });

        const currentUserId = userInfo?._id || userInfo?.id;

        if (currentUserId) {
          usersMap.set(String(currentUserId), userInfo);
        } else if (userInfo?.referralCode) {
          const existingRoot = Array.from(
            usersMap.values()
          ).find(
            (item) =>
              item?.referralCode === userInfo.referralCode
          );

          if (!existingRoot) {
            usersMap.set(
              `root-${userInfo.referralCode}`,
              userInfo
            );
          }
        }

        const users = Array.from(usersMap.values());

        setAllUsers(users);

        const rootUser = selectedUser || userInfo;

        const result = buildTree(rootUser, users);

        setTreeData(result);
      } catch (error) {
        console.error("Tree fetch error:", error);

        if (!cancelled) {
          setErrorMessage(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              "İstifadəçilər yüklənərkən xəta baş verdi."
          );

          setTreeData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTree();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, userInfo, selectedUser]);

  /*
  |--------------------------------------------------------------------------
  | WINDOW RESIZE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const handleResize = () => {
      setTranslate((previous) => ({
        ...previous,
        x: window.innerWidth / 2,
      }));
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | SEARCH SUGGESTIONS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const value = searchInput.trim().toLowerCase();

    if (!value) {
      setSuggestions([]);
      return;
    }

    const result = allUsers
      .filter((user) => {
        const name = String(user?.name || "").toLowerCase();

        const referralCode = String(
          user?.referralCode || ""
        ).toLowerCase();

        const email = String(
          user?.email || ""
        ).toLowerCase();

        return (
          name.includes(value) ||
          referralCode.includes(value) ||
          email.includes(value)
        );
      })

    setSuggestions(result);
  }, [searchInput, allUsers]);

  /*
  |--------------------------------------------------------------------------
  | NOTIFICATION AUTO CLOSE
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!successMessage && !errorMessage) {
      return;
    }

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 1500);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  /*
  |--------------------------------------------------------------------------
  | NOTIFICATION CLOSE ON OUTSIDE CLICK
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!successMessage && !errorMessage) {
      return;
    }

    const handleDocumentClick = () => {
      setSuccessMessage("");
      setErrorMessage("");
    };

    const timer = setTimeout(() => {
      document.addEventListener(
        "click",
        handleDocumentClick
      );
    }, 0);

    return () => {
      clearTimeout(timer);

      document.removeEventListener(
        "click",
        handleDocumentClick
      );
    };
  }, [successMessage, errorMessage]);

  /*
  |--------------------------------------------------------------------------
  | SEARCH USER
  |--------------------------------------------------------------------------
  */

  

  const handleSearch = async () => {
    const value = searchInput.trim().toLowerCase();

    if (!value) {
      setErrorMessage(
        "Axtarış üçün ad və ya referans kodu yazın."
      );

      return;
    }

    setSearchLoading(true);

    setErrorMessage("");
    setSuccessMessage("");

    try {
      const foundUser = allUsers.find((user) => {
        const name = String(user?.name || "").toLowerCase();

        const referralCode = String(
          user?.referralCode || ""
        ).toLowerCase();

        const email = String(
          user?.email || ""
        ).toLowerCase();

        return (
          name === value ||
          referralCode === value ||
          email === value
        );
      });

      if (!foundUser) {
        setErrorMessage(
          "Bu adda və ya referans kodunda istifadəçi tapılmadı."
        );

        return;
      }

      setSelectedUser(foundUser);

      // AXTARIŞDAN SONRA INPUTU TƏMİZLƏ
      setSearchInput("");

      setSuccessMessage(
        `${foundUser.name || "İstifadəçi"} tapıldı.`
      );

      setSuggestions([]);
    } catch (error) {
      console.error(error);

      setErrorMessage(
        "Axtarış zamanı xəta baş verdi."
      );
    } finally {
      setSearchLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | SUGGESTION CLICK
  |--------------------------------------------------------------------------
  */

  const handleSuggestionClick = (user) => {
    if (!user) {
      return;
    }

    setSelectedUser(user);

    // İSTİFADƏÇİ SEÇİLƏNDƏ SEARCH INPUTU TƏMİZLƏ
    setSearchInput("");

    setSuggestions([]);

    setErrorMessage("");

    setSuccessMessage(
      `${user.name || "İstifadəçi"} seçildi.`
    );
  };

  /*
  |--------------------------------------------------------------------------
  | RESET
  |--------------------------------------------------------------------------
  */

  const handleReset = () => {
    setSearchInput("");

    setSuggestions([]);

    setSelectedUser(null);

    setErrorMessage("");

    setSuccessMessage("");

    setZoom(0.9);

    setTranslate({
      x: window.innerWidth / 2,
      y: 110,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | COPY REFERRAL CODE
  |--------------------------------------------------------------------------
  */

  const copyReferralCode = async (code) => {
    if (!code || code === "-") {
      return;
    }

    try {
      if (
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(code);
      } else {
        const textarea =
          document.createElement("textarea");

        textarea.value = code;

        textarea.style.position = "fixed";
        textarea.style.opacity = "0";

        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();

        document.execCommand("copy");

        document.body.removeChild(textarea);
      }

      setCopiedReferralCode(code);

      setSuccessMessage(
        `"${code}" referans kodu kopyalandı.`
      );

      setTimeout(() => {
        setCopiedReferralCode(null);
      }, 2000);
    } catch (error) {
      console.error("Copy error:", error);

      setErrorMessage(
        "Referans kodu kopyalanmadı."
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | OPEN USER DETAILS
  |--------------------------------------------------------------------------
  */

  const handleOpenUserDetails = (userId) => {
    if (!userId) {
      setErrorMessage(
        "Bu istifadəçinin ID məlumatı tapılmadı."
      );

      return;
    }

    setDetailsUserId(userId);
  };

  const visibleUserCount = useMemo(() => {
  if (!treeData || !Array.isArray(treeData)) {
    return 0;
  }

  let count = 0;

  const countNodes = (nodes) => {
    if (!Array.isArray(nodes)) {
      return;
    }

    nodes.forEach((node) => {
      if (!node) {
        return;
      }

      count += 1;

      if (Array.isArray(node.children) && node.children.length > 0) {
        countNodes(node.children);
      }
    });
  };

  countNodes(treeData);

  return count;
}, [treeData]);

  /*
  |--------------------------------------------------------------------------
  | CUSTOM NODE
  |--------------------------------------------------------------------------
  */

  const renderCustomNode = ({ nodeDatum }) => {
    const attributes = nodeDatum?.attributes || {};

    const userId = attributes.UserId;

    const referralCode = attributes.Kod || "-";

    const photo = attributes.Photo;

    const payment = attributes.Ödəniş;

    const side = attributes.Qol;

    const name =
      nodeDatum?.name || "Adsız istifadəçi";

    const isPaid =
      payment === "Ödəniş edib";

    const isRoot =
      side === "Ana qol";

    const rectWidth = 300;

    const rectHeight = 158;

    return (
      <g>
        {/* Ana kart */}

        <rect
          x={-rectWidth / 2}
          y={-rectHeight / 2}
          width={rectWidth}
          height={rectHeight}
          rx={18}
          ry={18}
          fill={
            darkMode
              ? "#111827"
              : "#ffffff"
          }
          stroke={
            isRoot
              ? darkMode
                ? "#60a5fa"
                : "#2563eb"
              : darkMode
              ? "#374151"
              : "#d1d5db"
          }
          strokeWidth={
            isRoot ? 3 : 2
          }
          filter="url(#nodeShadow)"
        />

        <foreignObject
          x={-rectWidth / 2}
          y={-rectHeight / 2}
          width={rectWidth}
          height={rectHeight}
          style={{
            overflow: "visible",
            pointerEvents: "auto",
          }}
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: `${rectWidth}px`,
              height: `${rectHeight}px`,
              boxSizing: "border-box",
              padding: "11px",
              fontFamily:
                "Inter, Arial, sans-serif",
              pointerEvents: "auto",
            }}
          >
            <div
              className={`w-full h-full rounded-[14px] flex flex-col ${
                darkMode
                  ? "text-white"
                  : "text-gray-900"
              }`}
            >
              {/* USER TOP */}

              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 shrink-0 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center border border-gray-300 dark:border-gray-600">
                  {photo ? (
                    <img
                      src={photo}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaUser className="text-gray-500 dark:text-gray-300 text-lg" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div
                    className="font-bold text-[14px] truncate"
                    title={name}
                  >
                    {name}
                  </div>

                  <div
                    className={`text-[11px] mt-1 font-medium ${
                      isRoot
                        ? "text-blue-500"
                        : side === "Sol"
                        ? "text-emerald-500"
                        : side === "Sağ"
                        ? "text-purple-500"
                        : "text-gray-500"
                    }`}
                  >
                    {side}
                  </div>
                </div>

                <div
                  className={`shrink-0 text-[9px] font-bold px-2 py-1 rounded-full ${
                    isPaid
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  }`}
                >
                  {isPaid
                    ? "ÖDƏNİŞ OLUNUB"
                    : "ÖDƏNİŞ OLUNMAYIB"}
                </div>
              </div>

              {/* REFERRAL CODE */}

              <div className="mt-2.5 flex items-center justify-between gap-2 rounded-lg bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5">
                <div className="min-w-0">
                  <div className="text-[9px] text-gray-500 dark:text-gray-400">
                    Referans kodu
                  </div>

                  <div
                    className="text-[11px] font-semibold truncate"
                    title={referralCode}
                  >
                    {referralCode}
                  </div>
                </div>

                <button
                  type="button"
                  title="Referans kodunu kopyala"
                  onMouseDown={(event) =>
                    event.stopPropagation()
                  }
                  onTouchStart={(event) =>
                    event.stopPropagation()
                  }
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    copyReferralCode(
                      referralCode
                    );
                  }}
                  className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition ${
                    copiedReferralCode ===
                    referralCode
                      ? "bg-emerald-500 text-white"
                      : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-blue-500 hover:text-white"
                  }`}
                >
                  {copiedReferralCode ===
                  referralCode ? (
                    <FaCheckCircle size={13} />
                  ) : (
                    <FaCopy size={13} />
                  )}
                </button>
              </div>

              {/* INFO BUTTON */}

              <button
                type="button"
                onMouseDown={(event) =>
                  event.stopPropagation()
                }
                onTouchStart={(event) =>
                  event.stopPropagation()
                }
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  handleOpenUserDetails(
                    userId
                  );
                }}
                className="mt-2 w-full h-8 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-[11px] font-semibold flex items-center justify-center gap-2 transition"
              >
                <FaInfoCircle size={12} />

                Məlumatlara bax
              </button>
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center ${
          darkMode
            ? "bg-gray-950"
            : "bg-gray-50"
        }`}
      >
        <div className="flex flex-col items-center gap-4">
          <FaSpinner
            className="animate-spin text-blue-600"
            size={42}
          />

          <div
            className={`font-semibold ${
              darkMode
                ? "text-white"
                : "text-gray-800"
            }`}
          >
            İstifadəçilər yüklənir...
          </div>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | MAIN UI
  |--------------------------------------------------------------------------
  */

  return (
    <div
      className={`fixed inset-0 z-[90] overflow-hidden ${
        darkMode
          ? "bg-gray-950"
          : "bg-gray-50"
      }`}
    >
      <style>
        {`
          .cedvel-tree-wrapper .rd3t-link {
            fill: none !important;
            opacity: 1 !important;
            vector-effect: none !important;
            stroke-width: var(--tree-line-width, 2px) !important;
          }

          .cedvel-tree-wrapper.tree-light .rd3t-link,
          .cedvel-tree-wrapper.tree-light .tree-link,
          .cedvel-tree-wrapper.tree-light path.rd3t-link {
            stroke: #94a3b8 !important;
            stroke-width: var(--tree-line-width, 2px) !important;
            fill: none !important;
            opacity: 1 !important;
          }

          .cedvel-tree-wrapper.tree-dark .rd3t-link,
          .cedvel-tree-wrapper.tree-dark .tree-link,
          .cedvel-tree-wrapper.tree-dark .tree-link-dark,
          .cedvel-tree-wrapper.tree-dark path.rd3t-link,
          .cedvel-tree-wrapper.tree-dark path.tree-link {
            stroke: #60a5fa !important;
            stroke-width: var(--tree-line-width, 2px) !important;
            fill: none !important;
            opacity: 1 !important;
          }

          .cedvel-tree-wrapper svg .rd3t-link {
            fill: none !important;
          }

          .cedvel-tree-wrapper.tree-dark .rd3t-link {
            filter: drop-shadow(
              0 0 1.5px rgba(96, 165, 250, 0.8)
            );
          }
        `}
      </style>

      <svg
        width="0"
        height="0"
        className="absolute"
      >
        <defs>
          <filter
            id="nodeShadow"
            x="-30%"
            y="-30%"
            width="160%"
            height="160%"
          >
            <feDropShadow
              dx="0"
              dy="5"
              stdDeviation="7"
              floodOpacity="0.18"
            />
          </filter>
        </defs>
      </svg>

      {/* HEADER */}

      <div
        className={`absolute top-0 left-0 right-0 z-[110] border-b backdrop-blur-xl ${
          darkMode
            ? "bg-gray-950/90 border-gray-800"
            : "bg-white/90 border-gray-200"
        }`}
      >
        <div className="px-4 py-3">
          <div className="flex flex-col xl:flex-row xl:items-center gap-3">

            {/* LEFT */}

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
                  darkMode
                    ? "bg-gray-800 text-white hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title="Geri"
              >
                <FaArrowLeft size={14} />
              </button>

              <div>
                <h1
                  className={`text-lg font-bold ${
                    darkMode
                      ? "text-white"
                      : "text-gray-900"
                  }`}
                >
                  Referans şəbəkəsi
                </h1>

                <p
                  className={`text-xs ${
                    darkMode
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Sağ və sol qol sistemi
                </p>
              </div>
            </div>

            {/* SEARCH */}

            <div className="relative flex-1 max-w-2xl xl:mx-auto">
              <div
                className={`flex items-center gap-2 rounded-xl border px-3 h-11 ${
                  darkMode
                    ? "bg-gray-900 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <FaSearch
                  className="text-gray-400 shrink-0"
                  size={14}
                />

                <input
                  type="text"
                  value={searchInput}
                  onChange={(event) =>
                    setSearchInput(
                      event.target.value
                    )
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }

                    if (event.key === "Escape") {
                      setSuggestions([]);
                    }
                  }}
                  placeholder="Ad, email və ya referans kodu..."
                  className={`flex-1 min-w-0 bg-transparent outline-none text-sm ${
                    darkMode
                      ? "text-white placeholder:text-gray-500"
                      : "text-gray-900 placeholder:text-gray-400"
                  }`}
                />

                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setSuggestions([]);
                    }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <FaTimes size={13} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  className="h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                >
                  {searchLoading ? (
                    <FaSpinner
                      className="animate-spin"
                      size={12}
                    />
                  ) : (
                    <FaSearch size={12} />
                  )}

                  Axtar
                </button>
              </div>

              {/* SUGGESTIONS */}

          {suggestions.length > 0 && (
  <div
    className={`absolute max-h-[320px] top-[48px] left-0 right-0 z-[200] rounded-xl border shadow-2xl overflow-y-auto overflow-x-hidden ${
      darkMode
        ? "bg-gray-900 border-gray-700"
        : "bg-white border-gray-200"
    }`}
    style={{
      scrollbarWidth: "thin",
      scrollbarColor: darkMode
        ? "#4b5563 transparent"
        : "#cbd5e1 transparent",
    }}
  >
    {suggestions.map((user, index) => (
      <button
        type="button"
        key={user._id || user.id || index}
        onClick={() => handleSuggestionClick(user)}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all duration-150 border-b last:border-b-0 ${
          darkMode
            ? "hover:bg-gray-800 border-gray-800"
            : "hover:bg-gray-50 border-gray-100"
        }`}
      >
        <div className="w-9 h-9 min-w-[36px] rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center overflow-hidden">
          {user.photo ? (
            <img
              src={user.photo}
              alt={user.name || ""}
              className="w-full h-full object-cover"
            />
          ) : (
            <FaUser className="text-blue-600 dark:text-blue-300" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div
            className={`font-semibold text-sm truncate ${
              darkMode
                ? "text-white"
                : "text-gray-900"
            }`}
          >
            {user.name || "Adsız istifadəçi"}
          </div>

          <div className="text-xs text-gray-500 truncate mt-0.5">
            {user.referralCode || user.email || "-"}
          </div>
        </div>

        <div
          className={`shrink-0 text-[10px] px-2 py-1 rounded-full font-semibold ${
            user.payment
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
          }`}
        >
          {user.payment
            ? "Ödəniş olunub"
            : "Ödəniş olunmayıb"}
        </div>
      </button>
    ))}
  </div>
)}
            </div>

            {/* RIGHT BUTTONS */}

            <div className="flex items-center gap-2 shrink-0">
             <div
  className={`hidden lg:flex items-center gap-2 px-3 h-10 rounded-xl ${
    darkMode
      ? "bg-gray-900 text-gray-300"
      : "bg-gray-100 text-gray-600"
  }`}
>
  <FaUsers size={13} />

  <span className="text-xs font-semibold">
    {visibleUserCount} istifadəçi
  </span>
</div>

              <button
                type="button"
                onClick={handleReset}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
                  darkMode
                    ? "bg-gray-800 text-white hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title="Sıfırla"
              >
                <FaRedo size={13} />
              </button>

              <button
                type="button"
                onClick={toggleDarkMode}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
                  darkMode
                    ? "bg-gray-800 text-yellow-300 hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title="Tema"
              >
                {darkMode ? (
                  <BiSolidSun size={19} />
                ) : (
                  <BiSolidMoon size={19} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SUCCESS */}

      {successMessage && (
        <div
          className="absolute top-[90px] left-1/2 -translate-x-1/2 z-[150]"
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white shadow-xl text-sm font-semibold">
            <FaCheckCircle />

            <span>{successMessage}</span>

            <button
              type="button"
              onClick={() =>
                setSuccessMessage("")
              }
              className="ml-2 opacity-70 hover:opacity-100"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>
      )}

      {/* ERROR */}

      {errorMessage && (
        <div
          className="absolute top-[90px] left-1/2 -translate-x-1/2 z-[150]"
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-600 text-white shadow-xl text-sm font-semibold">
            <FaExclamationTriangle />

            <span>{errorMessage}</span>

            <button
              type="button"
              onClick={() =>
                setErrorMessage("")
              }
              className="ml-2 opacity-70 hover:opacity-100"
            >
              <FaTimes size={12} />
            </button>
          </div>
        </div>
      )}

      {/* TREE */}

      <div
        className={`cedvel-tree-wrapper absolute inset-0 pt-[88px] ${
          darkMode
            ? "tree-dark bg-gray-950"
            : "tree-light bg-gray-50"
        }`}
        style={{
          "--tree-line-width": `${Math.min(
            4,
            Math.max(
              0.65,
              1.8 / Math.max(zoom, 0.2)
            )
          )}px`,
        }}
      >
        {treeData ? (
          <Tree
            data={treeData}
            translate={translate}
            zoom={zoom}
            onZoom={setZoom}
            orientation="vertical"
            pathFunc="step"
            renderCustomNodeElement={
              renderCustomNode
            }
            nodeSize={{
              x: 320,
              y: 380,
            }}
            separation={{
              siblings: 1.1,
              nonSiblings: 1.3,
            }}
            enableLegacyTransitions={false}
            pathClassFunc={() =>
              darkMode
                ? "tree-link tree-link-dark"
                : "tree-link tree-link-light"
            }
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div
              className={`flex flex-col items-center gap-3 ${
                darkMode
                  ? "text-gray-300"
                  : "text-gray-600"
              }`}
            >
              <FaUsers size={38} />

              <div className="font-semibold">
                Göstəriləcək istifadəçi yoxdur
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ZOOM INFO */}

      <div
        className={`absolute bottom-5 left-5 z-[120] px-3 py-2 rounded-xl text-xs font-semibold shadow-lg ${
          darkMode
            ? "bg-gray-900 text-gray-300 border border-gray-800"
            : "bg-white text-gray-600 border border-gray-200"
        }`}
      >
        Zoom: {Math.round(zoom * 100)}%
      </div>

      {/* ZOOM CONTROLS */}

      <div
        className={`absolute bottom-5 right-5 z-[120] flex items-center gap-1 p-1 rounded-xl shadow-lg border ${
          darkMode
            ? "bg-gray-900 border-gray-800"
            : "bg-white border-gray-200"
        }`}
      >
        <button
          type="button"
          onClick={() =>
            setZoom((value) =>
              Math.min(2, value + 0.1)
            )
          }
          className={`w-9 h-9 rounded-lg text-lg font-bold ${
            darkMode
              ? "hover:bg-gray-800 text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          +
        </button>

        <button
          type="button"
          onClick={() =>
            setZoom((value) =>
              Math.max(0.2, value - 0.1)
            )
          }
          className={`w-9 h-9 rounded-lg text-lg font-bold ${
            darkMode
              ? "hover:bg-gray-800 text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          −
        </button>

        <button
          type="button"
          onClick={() => {
            setZoom(0.75);

            setTranslate({
              x: window.innerWidth / 2,
              y: 110,
            });
          }}
          className={`h-9 px-3 rounded-lg text-xs font-semibold ${
            darkMode
              ? "hover:bg-gray-800 text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          Mərkəz
        </button>
      </div>

      {/* USER DETAILS */}

      {detailsUserId && (
        <UserDetails
          user={allUsers.find(
            (item) =>
              (item._id || item.id) ===
              detailsUserId
          )}
          onClose={() =>
            setDetailsUserId(null)
          }
          isDarkMode={darkMode}
        />
      )}
    </div>
  );
};

export default Cedvel;
